import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import { api } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore';

// Fix para ícones do Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface OSAtiva {
  id: number;
  codigo_acesso: string;
  nome_falecido: string;
  status_geral: string;
  fases: Array<{
    id: number;
    nome_fase: string;
    status_fase: string;
    endereco_destino: string;
    motorista_nome?: string;
    motorista_email?: string;
    lat?: number;
    lng?: number;
  }>;
}

interface TrackingMapProps {
  className?: string;
  showHeader?: boolean;
}

const TrackingMap: React.FC<TrackingMapProps> = ({ className = "h-96", showHeader = true }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [osAtivas, setOSAtivas] = useState<OSAtiva[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const socketRef = useRef<Socket | null>(null);
  const markersRef = useRef<Map<number, L.Marker>>(new Map());
  const routesRef = useRef<Map<number, L.Polyline>>(new Map());
  const { token, isAuthenticated } = useAuthStore();

  // Função para calcular rota entre dois pontos
  const calculateRoute = async (origin: [number, number], destination: string) => {
    try {
      // Usar o serviço de geocoding para obter coordenadas do destino
      const geocodingResponse = await api.get(`/geocoding/geocode?address=${encodeURIComponent(destination)}`);
      
      if (geocodingResponse.data.success && geocodingResponse.data.data) {
        const destCoords = geocodingResponse.data.data;
        
        // Usar o serviço de roteamento para calcular a rota
        const routingResponse = await api.get(`/routing/route?origin=${origin[0]},${origin[1]}&destination=${destCoords.lat},${destCoords.lng}`);
        
        if (routingResponse.data.success && routingResponse.data.data) {
          return routingResponse.data.data.route;
        }
      }
    } catch (error) {
      console.error('Erro ao calcular rota:', error);
    }
    return null;
  };

  // Função para criar ícone personalizado do motorista
  const createDriverIcon = (driverName: string) => {
    return L.divIcon({
      className: 'driver-marker',
      html: `
        <div style="
          background: linear-gradient(145deg, #1E293B 0%, #0F172A 100%);
          width: 48px;
          height: 48px;
          border-radius: 12px;
          border: 2px solid rgba(255,255,255,0.9);
          box-shadow: 
            0 6px 20px rgba(0,0,0,0.15),
            0 0 0 1px rgba(255,255,255,0.2),
            inset 0 1px 0 rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          position: relative;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        ">
          🚗
          <div style="
            position: absolute;
            bottom: -6px;
            left: 50%;
            transform: translateX(-50%);
            width: 0;
            height: 0;
            border-left: 8px solid transparent;
            border-right: 8px solid transparent;
            border-top: 6px solid #1E293B;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          "></div>
        </div>
      `,
      iconSize: [48, 48],
      iconAnchor: [24, 54],
    });
  };

  // Função para carregar OSs ativas com posições de rastreamento
  const loadOSAtivas = async () => {
    try {
      setIsLoading(true);
      
      // Usar o novo endpoint de rastreamento para obter posições ativas
      const response = await api.get('/rastreamento/posicoes-ativas');
      console.log('📡 Resposta completa da API:', response.data);
      const posicoesData = response.data.data?.posicoes || [];
      
      console.log('Posições ativas carregadas:', posicoesData);
      
      // Log detalhado de cada posição para debug
      posicoesData.forEach((posicao: any, index: number) => {
        console.log(`📍 Posição ${index + 1}:`, {
          os_id: posicao.os_id,
          fase_id: posicao.fase_id,
          codigo_acesso: posicao.codigo_acesso,
          nome_falecido: posicao.nome_falecido,
          nome_fase: posicao.nome_fase,
          status_fase: posicao.status_fase,
          motorista_nome: posicao.motorista_nome,
          ultima_lat: posicao.ultima_lat,
          ultima_lng: posicao.ultima_lng,
          ultima_atualizacao: posicao.ultima_atualizacao,
          status_rastreamento: posicao.status_rastreamento
        });
      });
      
      // Transformar dados para o formato esperado pelo mapa (lat/lng podem ser null se ainda não chegou primeira posição)
      const osComPosicoes = posicoesData.map((posicao: any) => ({
        id: posicao.os_id,
        codigo_acesso: posicao.codigo_acesso,
        nome_falecido: posicao.nome_falecido,
        status_geral: posicao.status_fase,
        fases: [{
          id: posicao.fase_id,
          nome_fase: posicao.nome_fase,
          status_fase: posicao.status_fase,
          endereco_destino: '',
          motorista_nome: posicao.motorista_nome,
          motorista_email: '',
          lat: posicao.ultima_lat != null ? parseFloat(posicao.ultima_lat) : null,
          lng: posicao.ultima_lng != null ? parseFloat(posicao.ultima_lng) : null,
          ultima_atualizacao: posicao.ultima_atualizacao,
          status_rastreamento: posicao.status_rastreamento
        }]
      }));
      
      console.log('OSs com posições de rastreamento:', osComPosicoes);
      
      setOSAtivas(osComPosicoes);
    } catch (error) {
      console.error('Erro ao carregar posições ativas:', error);
      
      // Fallback: carregar OSs normais se o endpoint falhar
      try {
        const fallbackResponse = await api.get('/os');
        const osData = fallbackResponse.data.os || [];
        
        const osComFasesAtivas = osData.filter((os: any) => {
          if (!os.fases) return false;
          return os.fases.some((fase: any) => fase.status_fase === 'Em Rota');
        });
        
        setOSAtivas(osComFasesAtivas);
      } catch (fallbackError) {
        console.error('Erro no fallback:', fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Função para conectar ao WebSocket
  const connectWebSocket = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    // Conectar ao WebSocket com autenticação
    console.log('🔌 Conectando ao WebSocket do admin...');
    const backendUrl = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3002';
    console.log('🌐 URL:', backendUrl);
    console.log('🔑 Token disponível:', !!token);
    
    socketRef.current = io(backendUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      timeout: 10000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });
    
    // Escutar atualizações de posição do novo sistema de rastreamento
    socketRef.current.on('posicao_motorista_atualizada', (data) => {
      console.log('📍 Posição do motorista atualizada via WebSocket:', data);
      
      // Atualizar posição no estado
      setOSAtivas(prevOS => prevOS.map(os => {
        if (os.id === data.osId) {
          return {
            ...os,
            fases: os.fases.map(fase => 
              fase.id === data.faseId 
                ? { 
                    ...fase, 
                    lat: data.lat, 
                    lng: data.lng,
                    ultima_atualizacao: data.timestamp
                  }
                : fase
            )
          };
        }
        return os;
      }));
      
      // Atualizar marcador no mapa
      updateMarkerPosition(data.faseId, data.lat, data.lng);
      
      // Mostrar notificação de atualização
      console.log(`🚗 Motorista ${data.osId} atualizou posição: ${data.lat}, ${data.lng}`);
    });

    // Escutar mudanças de status
    socketRef.current.on('status_atualizado', (data) => {
      console.log('🔄 Status atualizado via WebSocket:', data);
      
      // Recarregar OSs ativas quando houver mudança de status
      loadOSAtivas();
    });

    // Entrar na sala de admin para receber atualizações
    socketRef.current.on('connect', () => {
      socketRef.current?.emit('join_admin_room');
      console.log('✅ WebSocket conectado - entrando na sala de admin');
    });
    
    // Tentar entrar na sala imediatamente se já estiver conectado
    if (socketRef.current.connected) {
      socketRef.current.emit('join_admin_room');
    }
    
    console.log('✅ WebSocket configurado para rastreamento em tempo real');
  };

  // Função para exibir rota no mapa
  const displayRoute = (faseId: number, route: any) => {
    if (!mapInstanceRef.current) return;
    
    // Remover rota anterior se existir
    const existingRoute = routesRef.current.get(faseId);
    if (existingRoute) {
      mapInstanceRef.current.removeLayer(existingRoute);
    }
    
    // Criar nova rota
    if (route && route.coordinates && route.coordinates.length > 0) {
      const routeCoords = route.coordinates.map((coord: any) => [coord.lat, coord.lng]);
      const polyline = L.polyline(routeCoords, {
        color: '#3B82F6',
        weight: 4,
        opacity: 0.8,
        dashArray: '10, 5'
      }).addTo(mapInstanceRef.current);
      
      // Armazenar referência à rota
      routesRef.current.set(faseId, polyline);
      
      // Adicionar marcador de destino
      const destMarker = L.marker(routeCoords[routeCoords.length - 1], {
        icon: L.divIcon({
          className: 'destination-marker',
          html: `
            <div style="
              background: #EF4444;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              color: white;
            ">
              🎯
            </div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        })
      }).addTo(mapInstanceRef.current);
      
      // Adicionar popup com informações da rota
      polyline.bindPopup(`
        <div class="p-3 min-w-48">
          <h4 class="font-semibold text-gray-900 mb-2">Rota para Destino</h4>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Distância:</span>
              <span class="font-medium">${route.distance || 'N/A'}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Tempo estimado:</span>
              <span class="font-medium">${route.duration || 'N/A'}</span>
            </div>
          </div>
        </div>
      `);
    }
  };

  // Função para atualizar posição do marcador
  const updateMarkerPosition = (faseId: number, lat: number, lng: number) => {
    const marker = markersRef.current.get(faseId);
    if (marker && mapInstanceRef.current) {
      marker.setLatLng([lat, lng]);
      
      // Atualizar rota se existir
      const existingRoute = routesRef.current.get(faseId);
      if (existingRoute) {
        // Recalcular rota com nova posição
        const os = osAtivas.find(os => os.fases.some(f => f.id === faseId));
        if (os) {
          const fase = os.fases.find(f => f.id === faseId);
          if (fase && fase.endereco_destino) {
            calculateRoute([lat, lng], fase.endereco_destino).then(route => {
              if (route) {
                displayRoute(faseId, route);
              }
            });
          }
        }
      }
      
      // Ajustar view para mostrar o marcador atualizado
      const bounds = mapInstanceRef.current.getBounds();
      bounds.extend([lat, lng]);
      mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });
    }
  };

  // Função para criar marcadores no mapa
  const createMapMarkers = (map: L.Map) => {
    console.log('🗺️ Criando marcadores no mapa...');
    console.log('🗺️ OSs ativas:', osAtivas);
    
    osAtivas.forEach((os, index) => {
      console.log(`🗺️ Processando OS ${index + 1}:`, os);
      
      // Encontrar fase em andamento
      const faseAtiva = os.fases.find(fase => 
        fase.status_fase === 'Em Rota'
      );
      
      console.log(`🗺️ Fase ativa encontrada para OS ${index + 1}:`, faseAtiva);
      
      if (faseAtiva && faseAtiva.lat && faseAtiva.lng) {
        const lat = parseFloat(faseAtiva.lat);
        const lng = parseFloat(faseAtiva.lng);
        
        console.log(`🗺️ Coordenadas convertidas: lat=${lat} (${typeof lat}), lng=${lng} (${typeof lng})`);
        
        console.log(`🗺️ Criando marcador para fase ${faseAtiva.id} em ${lat}, ${lng}`);
        
        const marker = L.marker([lat, lng], {
          icon: createDriverIcon(faseAtiva.motorista_nome || 'Motorista')
        }).addTo(map);

        // Armazenar referência ao marcador
        markersRef.current.set(faseAtiva.id, marker);
        
        console.log(`✅ Marcador criado e adicionado ao mapa para fase ${faseAtiva.id}`);
        
        // Criar popup com informações da OS e rastreamento
        const ultimaAtualizacao = faseAtiva.ultima_atualizacao 
          ? new Date(faseAtiva.ultima_atualizacao).toLocaleString('pt-BR')
          : 'Não disponível';
          
        const popupContent = `
          <div class="p-4 min-w-64">
            <div class="flex items-center space-x-3 mb-3">
              <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                🚗
              </div>
              <div>
                <h3 class="font-semibold text-gray-900">${faseAtiva.motorista_nome || 'Motorista'}</h3>
                <p class="text-sm text-gray-600">Motorista</p>
              </div>
            </div>
            
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">OS:</span>
                <span class="text-sm text-gray-900">${os.codigo_acesso}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">Falecido:</span>
                <span class="text-sm text-gray-900">${os.nome_falecido}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">Fase:</span>
                <span class="text-sm text-blue-600 font-medium">${faseAtiva.nome_fase}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">Destino:</span>
                <span class="text-sm text-gray-900">${faseAtiva.endereco_destino || 'Não informado'}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">Status:</span>
                <span class="text-sm text-blue-600 font-medium">${faseAtiva.status_fase}</span>
              </div>
            </div>
            
            <hr class="my-3 border-gray-200">
            
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">📍 Posição:</span>
                <span class="text-sm text-gray-900 font-mono">${lat.toFixed(6)}, ${lng.toFixed(6)}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">🕒 Última Atualização:</span>
                <span class="text-sm text-gray-900">${ultimaAtualizacao}</span>
              </div>
              
              <div class="flex justify-between">
                <span class="text-sm font-medium text-gray-700">📡 Rastreamento:</span>
                <span class="text-sm font-medium ${
                  faseAtiva.status_rastreamento === 'ativo' 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }">
                  ${faseAtiva.status_rastreamento === 'ativo' ? '🟢 Ativo' : '🔴 Inativo'}
                </span>
              </div>
            </div>
            
            <hr class="my-3 border-gray-200">
            
            <div class="text-center">
              <button 
                onclick="window.calculateRouteForOS(${faseAtiva.id}, '${faseAtiva.endereco_destino}')"
                class="w-full bg-blue-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                🗺️ Calcular Rota
              </button>
            </div>
          </div>
        `;

        marker.bindPopup(popupContent);
        
        // Calcular e exibir rota automaticamente se houver endereço de destino
        if (faseAtiva.endereco_destino) {
          calculateRoute([lat, lng], faseAtiva.endereco_destino).then(route => {
            if (route) {
              displayRoute(faseAtiva.id, route);
            }
          });
        }
      } else {
        console.log(`❌ Fase ${faseAtiva?.id} não tem coordenadas válidas:`, {
          faseAtiva,
          temLat: !!faseAtiva?.lat,
          temLng: !!faseAtiva?.lng,
          lat: faseAtiva?.lat,
          lng: faseAtiva?.lng
        });
      }
    });
  };

  // Carregar posições iniciais
  useEffect(() => {
    loadOSAtivas();
  }, []);

  // Polling periódico como fallback (a cada 15 segundos)
  useEffect(() => {
    const pollingInterval = setInterval(() => {
      // Só fazer polling se WebSocket não estiver conectado ou como backup
      const isSocketConnected = socketRef.current?.connected || false;
      
      console.log('🔄 Polling periódico executando...', {
        socketConnected: isSocketConnected,
        osAtivasCount: osAtivas.length
      });
      
      // Fazer polling mesmo com WebSocket ativo (para sincronização)
      loadOSAtivas();
    }, 15000); // 15 segundos

    return () => {
      clearInterval(pollingInterval);
    };
  }, []);

  // Conectar ao WebSocket quando osAtivas mudar
  useEffect(() => {
    if (osAtivas.length > 0) {
      connectWebSocket();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [osAtivas]);

  // Função global para calcular rotas (acessível via popup)
  useEffect(() => {
    (window as any).calculateRouteForOS = async (faseId: number, destination: string) => {
      const os = osAtivas.find(os => os.fases.some(f => f.id === faseId));
      if (os) {
        const fase = os.fases.find(f => f.id === faseId);
        if (fase && fase.lat && fase.lng) {
          const route = await calculateRoute([fase.lat, fase.lng], destination);
          if (route) {
            displayRoute(faseId, route);
          }
        }
      }
    };

    return () => {
      delete (window as any).calculateRouteForOS;
    };
  }, [osAtivas]);

  // Conectar ao WebSocket automaticamente quando o componente montar
  useEffect(() => {
    // Tentar conectar ao WebSocket automaticamente
    if (isAuthenticated && token) {
      console.log('🔌 Tentando conectar ao WebSocket automaticamente...');
      console.log('🔑 Token disponível:', !!token);
      connectWebSocket();
    } else {
      console.log('⚠️ Admin não autenticado ou token não disponível');
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    console.log('🔍 useEffect do mapa executando...');
    console.log('🔍 mapRef.current:', !!mapRef.current);
    console.log('🔍 osAtivas.length:', osAtivas.length);
    console.log('🔍 osAtivas:', osAtivas);
    
    if (!mapRef.current || osAtivas.length === 0) {
      console.log('🔍 Retornando cedo - mapRef ou osAtivas vazio');
      return;
    }

    console.log('🔍 Criando mapa...');
    
    // Criar mapa
    console.log('🔍 Tentando criar mapa Leaflet...');
    const map = L.map(mapRef.current).setView([-23.5505, -46.6333], 10); // São Paulo
    console.log('🔍 Mapa criado:', !!map);
    mapInstanceRef.current = map;

    // Adicionar camada OpenStreetMap
    console.log('🔍 Adicionando camada OpenStreetMap...');
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);
    console.log('🔍 Camada OpenStreetMap adicionada');

    // Criar marcadores
    createMapMarkers(map);

    // Ajustar view para mostrar todos os marcadores
    if (osAtivas.length > 0) {
      const bounds = L.latLngBounds([]);
      osAtivas.forEach((os) => {
        const faseAtiva = os.fases.find(fase => 
          fase.status_fase === 'Em Rota'
        );
        if (faseAtiva && faseAtiva.lat != null && faseAtiva.lng != null) {
          bounds.extend([Number(faseAtiva.lat), Number(faseAtiva.lng)]);
        }
      });
      
      if (bounds.getNorthEast() && bounds.getSouthWest()) {
        map.fitBounds(bounds, { padding: [20, 20] });
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        // Limpar todas as rotas
        routesRef.current.forEach((route) => {
          mapInstanceRef.current?.removeLayer(route);
        });
        routesRef.current.clear();
        
        mapInstanceRef.current.remove();
      }
    };
  }, [osAtivas]);

  return (
    <div className={`${showHeader ? 'bg-white rounded-lg shadow-lg overflow-hidden' : 'h-full w-full bg-white'} ${className}`} style={!showHeader ? { margin: 0, padding: 0 } : {}}>
      {showHeader && (
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-blue-600" />
            Rastreamento em Tempo Real
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {osAtivas.length} motorista(s) em rota
          </p>
        </div>
      )}
      
      <div className={`relative ${showHeader ? 'h-80' : 'h-full'}`}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full py-12">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-gray-600">Carregando mapa...</p>
            </div>
          </div>
        ) : osAtivas.length === 0 ? (
          <div className="flex items-center justify-center h-full py-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="h-6 w-6 text-gray-400" />
            </div>
              <p className="text-sm text-gray-600">Nenhum motorista em rota no momento</p>
              <p className="text-xs text-gray-400 mt-1">Debug: osAtivas.length = {osAtivas.length}</p>
            </div>
          </div>
        ) : (
          <div ref={mapRef} className="w-full h-full" style={{ margin: 0, padding: 0, overflow: 'hidden' }} />
        )}
      </div>
    </div>
  );
};

export default TrackingMap;
